import React, { useEffect } from 'react';
import { Box, Flex, Link, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useCustomer } from '../../../../contexts/useCustomer';
import { useNavigation } from '../../../../hooks';
import { useScrollListener } from '../../../../hooks/useScrollListener';
import { PageUrl } from '../../../../pages/utils/page-url';
import { Button, Text } from '../../../../ui-components';
import { Popover } from '../../../../ui-components/popover';
import { useCustomPreferenceOrDefault, useUrl } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { isHydrated } from '../../../../utils/utils';
import { ConditionalRender } from '../../../conditionally-render/ConditionalRender';
import {
    AccountIcon,
    GiftRegistryIcon,
    OrderIcon,
    PerksIcon,
    UserIcon,
    WishListIcon,
} from '../../../icons';

export const MenuCustomer: React.FC = () => {
    const { customer, isRegistered, isLoading, logout } = useCustomer();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const showAccountUrl = useUrl(PageUrl.ACCOUNT_SHOW);
    const ordersUrl = useUrl(PageUrl.ORDERS_HISTORY);
    const wishListUrl = useUrl(PageUrl.WISHLIST);
    const giftRegistryUrl = useUrl(PageUrl.GIFT_REGISTRY_START);
    const sfccLogoutUrl = useUrl(PageUrl.LOGOUT);
    const navigate = useNavigation();
    const pwaAccountRedirect = useCustomPreferenceOrDefault(
        CustomPreference.PWA_ACCOUNT_REDIRECT,
        true,
    );
    const { isNotScrolled } = useScrollListener();

    const accountUrl = pwaAccountRedirect ? PageUrl.ACCOUNT_LOGIN : showAccountUrl;

    useEffect(() => onClose, []);

    const onSignIn = (): void => {
        navigate(accountUrl);
    };

    const onSignOut = async (): Promise<void> => {
        await logout.mutateAsync();

        navigate(sfccLogoutUrl);
    };

    if (isLoading || !isHydrated()) {
        return null;
    }

    return (
        <Popover
            id="header.menu.customer"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            triggerElement={
                <Flex alignItems="center" gap={1} cursor="pointer">
                    <UserIcon data-testid="header.menu.customer.icon" margin={1} />

                    <ConditionalRender visibility={{ base: false, md: isNotScrolled }}>
                        <Box
                            height="1.625rem"
                            _hover={{ borderBottom: '2px solid', borderColor: 'slt-pepper.1000' }}
                        >
                            <Text id="header.menu.customer.label">
                                {isRegistered ? (
                                    <>
                                        <FormattedMessage id="header.hi" defaultMessage="Hi" />
                                        {', '}
                                        {customer?.firstName}
                                    </>
                                ) : (
                                    <FormattedMessage id="sign.in" defaultMessage="Sign In" />
                                )}
                            </Text>
                        </Box>
                    </ConditionalRender>
                </Flex>
            }
            headerElement={
                isRegistered ? (
                    <Flex alignItems="center" justifyContent="space-between">
                        <Text id="header.menu.customer.logged_in.name" size="large-2">
                            <FormattedMessage id="header.hi" defaultMessage="Hi" />
                            {', '}
                            {customer?.firstName}
                        </Text>

                        <Button
                            id="header.menu.customer.sign_out.button"
                            variant="tertiary"
                            onClick={onSignOut}
                            isLoading={logout.isLoading}
                            className="!decoration-slt-pepper-1000"
                            autoFocus
                        >
                            <FormattedMessage id="sign.out" defaultMessage="Sign out" />
                        </Button>
                    </Flex>
                ) : (
                    <Flex data-testid="header.menu.customer.guest" direction="column" gap={4}>
                        <Flex direction="column" gap={1}>
                            <Text id="header.menu.customer.guest.title" size="large-2">
                                <FormattedMessage id="hey.chef" defaultMessage="Hey Chef," />
                            </Text>

                            <Text id="header.menu.customer.guest.subtitle" style="regular">
                                <FormattedMessage
                                    id="sign.in.for.a.personalized.cooking.experience"
                                    defaultMessage="Sign in for a personalized cooking experience"
                                />
                            </Text>
                        </Flex>

                        <Button
                            id="header.menu.customer.sign_in.button"
                            variant="primary-black"
                            onClick={onSignIn}
                            autoFocus
                        >
                            <FormattedMessage
                                id="sign.in.or.create.account"
                                defaultMessage="Sign In | Create Account"
                            />
                        </Button>
                    </Flex>
                )
            }
            bodyElement={
                <>
                    <Flex
                        data-testid="header.menu.customer.perks"
                        backgroundColor="slt-beige.400"
                        gap={2}
                        paddingY={1}
                        paddingX={4}
                    >
                        <PerksIcon
                            data-testid="header.menu.customer.perks.icon"
                            width={6}
                            height={6}
                            margin={1}
                        />

                        <Flex direction="column">
                            <Text id="header.menu.customer.perks.title">
                                <FormattedMessage id="slt.perks" defaultMessage="SLT Perks" />
                            </Text>

                            <Text
                                id="header.menu.customer.perks.subtitle"
                                size="small"
                                color="slt-pepper.800"
                            >
                                <FormattedMessage
                                    id="earn.perks.with.your.next.purchase"
                                    defaultMessage="Earn perks with your next purchase."
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    <Link
                        data-testid="header.menu.customer.my_orders.link"
                        href={isRegistered ? ordersUrl : PageUrl.TRACK_ORDER}
                        width="fit-content"
                        _hover={{ textDecoration: 'none' }}
                    >
                        <Flex gap={2} paddingX={4}>
                            <OrderIcon
                                data-testid="header.menu.customer.my_orders.icon"
                                width={6}
                                height={6}
                                margin={1}
                            />

                            <Flex direction="column">
                                <Text id="header.menu.customer.my_orders.title">
                                    <FormattedMessage id="my.orders" defaultMessage="My Orders" />
                                </Text>

                                <Text
                                    id="header.menu.customer.my_orders.subtitle"
                                    size="small"
                                    color="slt-pepper.800"
                                >
                                    <FormattedMessage
                                        id="view.and.track.online.purchases"
                                        defaultMessage="View and track online purchases"
                                    />
                                </Text>
                            </Flex>
                        </Flex>
                    </Link>

                    <Link
                        data-testid="header.menu.customer.gift_registries.link"
                        href={giftRegistryUrl}
                        width="fit-content"
                        _hover={{ textDecoration: 'none' }}
                    >
                        <Flex gap={2} paddingX={4}>
                            <GiftRegistryIcon
                                data-testid="header.menu.customer.gift_registries.icon"
                                width={6}
                                height={6}
                                margin={1}
                            />

                            <Flex direction="column">
                                <Text id="header.menu.customer.gift_registries.title">
                                    <FormattedMessage
                                        id="gift.registries"
                                        defaultMessage="Gift Registries"
                                    />
                                </Text>

                                <Text
                                    id="header.menu.customer.gift_registries.subtitle"
                                    size="small"
                                    color="slt-pepper.800"
                                >
                                    <FormattedMessage
                                        id="create.and.manage.registries"
                                        defaultMessage="Create and manage registries"
                                    />
                                </Text>
                            </Flex>
                        </Flex>
                    </Link>

                    <Link
                        data-testid="header.menu.customer.wish_list.link"
                        href={
                            isRegistered
                                ? wishListUrl
                                : `${accountUrl}?redirect-url=${encodeURIComponent(wishListUrl)}`
                        }
                        width="fit-content"
                        _hover={{ textDecoration: 'none' }}
                    >
                        <Flex gap={2} paddingX={4}>
                            <WishListIcon
                                data-testid="header.menu.customer.wish_list.icon"
                                width={6}
                                height={6}
                                margin={1}
                            />

                            <Flex direction="column">
                                <Text id="header.menu.customer.wish_list.title">
                                    <FormattedMessage id="wish.list" defaultMessage="Wish List" />
                                </Text>

                                <Text
                                    id="header.menu.customer.wish_list.subtitle"
                                    size="small"
                                    color="slt-pepper.800"
                                >
                                    <FormattedMessage
                                        id="view.saved.products"
                                        defaultMessage="View saved products"
                                    />
                                </Text>
                            </Flex>
                        </Flex>
                    </Link>

                    <Link
                        data-testid="header.menu.customer.my_account.link"
                        href={isRegistered ? showAccountUrl : accountUrl}
                        width="fit-content"
                        _hover={{ textDecoration: 'none' }}
                    >
                        <Flex gap={2} paddingX={4}>
                            <AccountIcon
                                data-testid="header.menu.customer.my_account.icon"
                                width={6}
                                height={6}
                                margin={1}
                            />

                            <Flex direction="column">
                                <Text id="header.menu.customer.my_account.title">
                                    <FormattedMessage id="my.account" defaultMessage="My Account" />
                                </Text>

                                <Text
                                    id="header.menu.customer.my_account.subtitle"
                                    size="small"
                                    color="slt-pepper.800"
                                >
                                    <FormattedMessage
                                        id="manage.payment.contact.info.addresses.password"
                                        defaultMessage="Manage payment, contact info, addresses, password"
                                    />
                                </Text>
                            </Flex>
                        </Flex>
                    </Link>
                </>
            }
        />
    );
};
